import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { filter, forkJoin, map, noop, Observable, take } from 'rxjs';

import { injectConfigProperty } from '@cosmos/config';
import { collectRouteData, collectRouteParams } from '@cosmos/router';
import { CustomerPortalAuthQueries } from '@customer-portal/auth/data-access-auth';
import { CustomerPortalPresentationQueries } from '@customer-portal/presentations/data-access-presentations';

export const enum PagesToTrackVisit {
  Proofs = 'proofs',
  Products = 'products',
  Order = 'order',
  Quote = 'quote',
  Invoice = 'invoice',
}

export interface NotificationPayload {
  presentationId?: string;
  assetType?: string;
  assetId?: string;
}

@Injectable({ providedIn: 'root' })
export class CustomerPortalVisitNotificationsService {
  private readonly _http = inject(HttpClient);
  private _router = inject(Router);
  private _store = inject(Store);
  private readonly _apiUrl: string = injectConfigProperty('marsApiUrl');

  constructor() {
    this._router.events
      .pipe(
        // listen to Navigation end to start analyzing the route tree
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          const rootSnapshot = this._router.routerState.root.snapshot;
          // Collect params and data from the root snapshot (the entire route tree),
          // since "visitedPage" may be defined on a parent route and should still be tracked.
          return {
            data: collectRouteData(rootSnapshot),
            params: collectRouteParams(rootSnapshot),
          };
        }),
        filter((routeCollectedData) => {
          return !!routeCollectedData.data['visitedPage'];
        }),
        // to send a notification once per visit
        take(1)
      )
      .subscribe((routeCollectedData) => {
        const payload: NotificationPayload = {};
        payload.assetType = routeCollectedData.data['visitedPage'];
        payload.assetId = routeCollectedData.params['documentId'] ?? '';
        const userIsDistributor = this._store.selectSnapshot(
          CustomerPortalAuthQueries.getUserIsDistributor
        );

        // prevent sending for the preview mode
        if (userIsDistributor) {
          return;
        }

        const presentationId$ = this._store
          .select(CustomerPortalPresentationQueries.getPresentationId)
          .pipe(
            filter((presentationId) => presentationId != null),
            take(1)
          );

        forkJoin({
          presentationId: presentationId$,
        }).subscribe(({ presentationId }) => {
          // It might return 500 by some reason, we do not handle it.
          this.sendNotification({
            ...payload,
            presentationId: presentationId!.toString(),
          }).subscribe({
            next: noop,
            error: noop,
          });
        });
      });
  }

  sendNotification(payload: NotificationPayload): Observable<void> {
    const params = new HttpParams({
      fromObject: payload as { [param: string]: string },
    });
    // Mars API expects the presentationId in the path, not the projectId.
    // Venus resolves the notification target using the presentationId.
    // See: https://github.com/asi/olympus/blob/master/services/mars/src/Mars.Web.Api/Controllers/ProjectsController.cs#L232
    return this._http.post<void>(
      `${this._apiUrl}/projects/${payload.presentationId}/viewed`,
      {},
      { params }
    );
  }
}
